import { Box, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Loading } from "src/components/utils/Loading";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler.ts";
import { useNavigate } from "react-router-dom";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { ScoreCard } from "@foodpilot/foods";
import { ScoreSummary } from "src/components/Score/ScoreCardContent/ScoreSummary";
import { Fragment } from "react/jsx-runtime";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useCompanyScores } from "src/context/Company/Scores/useCompanyScores";

export const CompanyScores = () => {
    const { scoreTypes, scores, getScoresByType } = useFoodpilotContext();
    const { company } = useCompanyMetadata();
    const { scoresTree, scoreState, allScoreWithVariations } = useCompanyScores();

    const { isFeatureActivated } = useApplicationParameters();

    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!scoreTypes || !scores) {
        return <Loading />;
    }

    const companiesScores = getScoresByType("company").filter(
        (st) => st.displayMode !== "rules" && st.displayMode !== "regulation",
    );

    if (companiesScores.length === 0) {
        return (
            <Stack gap="28px" sx={{ padding: "44px 52px" }}>
                <Box sx={{ textAlign: "center", padding: "28px" }}>{t("supplier.no-score")}</Box>
            </Stack>
        );
    }

    const isComparisonActive = isFeatureActivated("FS-score-details-comparison");
    const isDistributionActive = isFeatureActivated("FS-score-details-distribution");

    const canDrilldown = isComparisonActive || isDistributionActive;

    return (
        <>
            <Box sx={{ display: "flex", gap: "1rem", flexDirection: "column" }}>
                <Box sx={{ display: "flex", flexDirection: "row", gap: "20px", padding: "1rem 0", flexWrap: "wrap" }}>
                    {companiesScores.map((score) => {
                        const scoreOpts = allScoreWithVariations.filter(
                            (scoreVariation) => scoreVariation.group === score.title,
                        );
                        const selectedScore = scoreOpts.find((filteredScore) => {
                            const scoreVariation = scoreState.state.selectedVariations[score.id];
                            const doesScoreMatch = scoreVariation !== undefined;
                            const doesVariationMatch =
                                doesScoreMatch === false ? false : filteredScore.variationId === scoreVariation;

                            return doesScoreMatch && doesVariationMatch;
                        });

                        if (scoreOpts.length === 0) return;
                        const selectedOption = selectedScore ?? scoreOpts[0];

                        const scoreValue = scoresTree.scores?.[score.id];
                        const prevScore = scoresTree?.prevScores?.[score.id]?.score;
                        const prevCampaign = scoresTree.prevCampaignName;

                        return (
                            <Fragment key={`${score.id}-${selectedOption.rowId}`}>
                                <ScoreCard
                                    header={{
                                        selectedItemId: selectedOption.rowId,
                                        options: scoreOpts.map((d) => ({ ...d, id: d.rowId })),
                                    }}
                                    moreDetailAction={() => {
                                        navigate(URLHandler.company.score(company.id, score.id));
                                    }}
                                    disableFooter={canDrilldown === false}
                                >
                                    <ScoreSummary
                                        improvement={selectedOption.improvement}
                                        score={scoreValue?.score}
                                        prevScoreValue={prevScore}
                                        prevCampaign={prevCampaign}
                                        referenceScore={undefined}
                                        precision={selectedOption.precision ?? 0}
                                        unit={selectedOption.unit}
                                    />
                                </ScoreCard>
                            </Fragment>
                        );
                    })}
                </Box>
            </Box>
        </>
    );
};
